import { computed, inject, Injectable, signal } from '@angular/core'
import { firstValueFrom, map, Observable } from 'rxjs'
import { LoginResponse, NotificationChannel, RegistrationsService, UpdateResponse } from 'superlikers-microsites-api'
import { ValidationErrors } from '@angular/forms'

import { environment } from '@environments/environment'

import { CacheService } from '@core/persistence'
import { User } from '@core/data'
import { AuthApi } from '../auth-api/auth-api'
import { AuthRequirementsPoller } from '../auth-requirements-poller/auth-requirements-poller'
import { AuthError } from '../models'

/**
 * AuthService - Manages authentication state and user session
 *
 * Responsibility: Centralized authentication state management for the entire application.
 *
 * Rules:
 * - Stores current user as a signal (reactive)
 * - Initializes CacheService with current user (for cache validation)
 * - Provides session lifecycle methods (initialize, logout, update, login)
 * - Manages requirements polling lifecycle
 * - This is the single source of truth for "who is logged in"
 *
 * Usage:
 * - Call `initialize()` from the auth guard (for page refresh)
 * - Call `login()` for user login
 * - Access current user with `authService.currentUser()`
 * - Components auto-update when signal changes
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  #authApi = inject(AuthApi)
  #cacheService = inject(CacheService)
  #requirementsPoller = inject(AuthRequirementsPoller)
  #registrationsService = inject(RegistrationsService)

  /**
   * Current authenticated user.
   * Signal allows components to reactively update when user changes.
   */
  currentUser = signal<User | null>(null)

  /**
   * Whether the current participant has both the "admin" and "organizer" tags,
   * granting access to content management actions (e.g. administrar solicitudes,
   * crear publicación in Noticias/Reconocimientos).
   */
  isOrganizer = computed(() => {
    const tags = this.currentUser()?.tags ?? []
    return tags.includes('admin') && tags.includes('organizer')
  })

  /**
   * Login with credentials.
   * Initializes session immediately (user ready before redirect).
   * @param credentials - Login form data (e.g., { email, password })
   */
  async login(credentials: Record<string, string>): Promise<LoginResponse> {
    try {
      const response = await firstValueFrom(this.#authApi.login(credentials))
      await this.initialize() // Load user immediately and initialize cache
      return response
    } catch (err) {
      const httpError = err as { error?: AuthError }
      throw httpError.error ?? err
    }
  }

  /**
   * Initialize auth state.
   * Fetches current user from backend and sets up cache validation.
   * Starts requirements polling if user is authenticated.
   * Call this from the auth guard (for page refresh scenarios).
   */
  async initialize(): Promise<void> {
    const user = await firstValueFrom(this.#authApi.fetchCurrentUser())
    this.currentUser.set(user)

    // Only initialize cache if user is authenticated
    if (user) {
      await this.#cacheService.init(user)
      this.#requirementsPoller.start()
    }
  }

  async loadCurrentUser(): Promise<User | null> {
    const user = await firstValueFrom(this.#authApi.fetchCurrentUser())
    this.currentUser.set(user)

    return user
  }

  /**
   * Set current user and initialize cache.
   * Updates signal and cache validation in one atomic operation.
   * @param user - User to set as current (null for logged out state)
   */
  async setCurrentUser(user: User | null): Promise<void> {
    this.currentUser.set(user)
    await this.#cacheService.init(user)
  }

  /**
   * Update current user properties.
   * @param updates - Partial user properties to update
   */
  async updateCurrentUser(updates: Partial<User>): Promise<UpdateResponse> {
    const response$ = this.#registrationsService.updateParticipant(updates)
    return firstValueFrom(response$)
  }

  /**
   * Logout current user.
   * Clears user signal, cache, and stops requirements polling.
   */
  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.#authApi.logout())
    } finally {
      window.location.reload()

      setTimeout(() => {
        this.currentUser.set(null)
        this.#requirementsPoller.stop()
      }, 100)
    }
  }

  validateChannel(channel: NotificationChannel, value: string): Observable<ValidationErrors | null> {
    return this.#authApi.validateChannel(channel, value).pipe(
      map(response => {
        if (response.state === 'error') {
          return { custom: response.message }
        }
        return null
      })
    )
  }

  async sendPasswordResetToken(data: Record<string, string>): Promise<void> {
    const redirectUrl = `${environment.id}/password/edit`
    const response = await firstValueFrom(this.#authApi.sendToken('email', data, redirectUrl))

    if (response.state !== 'success') {
      throw new Error(response.message ?? 'Error al enviar el correo de recuperación')
    }
  }

  /**
   * Check if user is authenticated.
   */
  isAuthenticated(): boolean {
    return this.currentUser() !== null
  }
}
